import { z } from "zod";

import { isCommercialPlanKey } from "./catalog";
import {
  CommercialExtraUsageMode,
  CommercialMeterKey,
  CommercialMeterKind,
  CommercialMigrationState,
  CommercialPendingPlanChangeStatus,
  CommercialUsageStatus,
  type CommercialPlanKey as CommercialPlanKeyType,
  type CommercialPlanSnapshot,
  type CommercialUsageMeterSnapshot,
} from "./types";

const COMMERCIAL_METER_KEYS = Object.values(CommercialMeterKey);

const commercialUsageMeterSchema = z.object({
  allowance: z.number().nonnegative().nullable(),
  current: z.number().nonnegative().nullish(),
  kind: z.enum([CommercialMeterKind.Cumulative, CommercialMeterKind.Gauge]),
  peak: z.number().nonnegative().nullish(),
  reset_at: z.string().nullish(),
  used: z.number().nonnegative(),
});

const commercialUsageMetersSchema = z.object({
  [CommercialMeterKey.UserRequests]: commercialUsageMeterSchema,
  [CommercialMeterKey.NewNotes]: commercialUsageMeterSchema,
  [CommercialMeterKey.ActiveNotes]: commercialUsageMeterSchema,
  [CommercialMeterKey.OpenTasksProjects]: commercialUsageMeterSchema,
  [CommercialMeterKey.ActiveCustomRoutines]: commercialUsageMeterSchema,
  [CommercialMeterKey.RoutineExecutions]: commercialUsageMeterSchema,
  [CommercialMeterKey.ActiveConnections]: commercialUsageMeterSchema,
  [CommercialMeterKey.VoiceModeHours]: commercialUsageMeterSchema,
});

const commercialPlanSnapshotSchema = z.object({
  block_policy: z
    .object({
      available: z.boolean(),
      complimentary_blocks: z.number().int().nonnegative(),
      max_paid_blocks: z.number().int().nonnegative().nullish(),
      mode: z.enum([CommercialExtraUsageMode.PauseAndAsk, CommercialExtraUsageMode.AutoExpand]),
      paid_blocks: z.number().int().nonnegative(),
      price_cents: z.number().int().nonnegative(),
    })
    .nullish(),
  catalog_generation: z.string().min(1),
  mem_pro_trial_eligible: z.boolean().default(false),
  current_plan: z.object({
    currency: z.literal("usd"),
    display_name: z.string().min(1),
    is_legacy: z.boolean(),
    key: z.string().min(1),
    monthly_price_cents: z.number().int().nonnegative().nullable(),
  }),
  meters: commercialUsageMetersSchema,
  migration: z
    .object({
      changed_allowances: z
        .array(
          z.object({
            change_kind: z.string(),
            current_display: z.string(),
            meter_key: z.enum(COMMERCIAL_METER_KEYS as [string, ...string[]]),
            new_allowance: z.number().nonnegative(),
          })
        )
        .optional(),
      eligible: z.boolean(),
      can_return: z.boolean(),
      limiting_meter_key: z.enum(COMMERCIAL_METER_KEYS as [string, ...string[]]).nullish(),
      recommended_paid_extra_usage_blocks: z.number().int().nonnegative().nullish(),
      recommended_plan_key: z.string().nullish(),
      source_monthly_price_cents: z.number().int().nonnegative().nullish(),
      source_plan_display_name: z.string().nullish(),
      state: z.enum(Object.values(CommercialMigrationState) as [string, ...string[]]),
    })
    .nullish(),
  paused_connection_ids: z.array(z.string().uuid()).default([]),
  paused_working_memory_items: z
    .array(
      z.object({
        item_id: z.string().uuid(),
        item_kind: z.enum(["assistant_task", "assistant_project"]),
      })
    )
    .default([]),
  pending_change: z
    .object({
      destination_plan_key: z.string().nullish(),
      effective_at: z.string(),
      paid_extra_usage_blocks: z.number().int().nonnegative().nullish(),
      status: z.enum(Object.values(CommercialPendingPlanChangeStatus)),
    })
    .nullish(),
  period: z.object({
    ends_at: z.string(),
    starts_at: z.string(),
  }),
});

const getUsageStatus = ({
  allowance,
  current,
  kind,
  used,
}: z.infer<typeof commercialUsageMeterSchema>): CommercialUsageStatus => {
  const displayUsage = kind === CommercialMeterKind.Gauge ? (current ?? used) : used;
  if (allowance === null) {
    return CommercialUsageStatus.Available;
  }

  if (allowance === 0) {
    return displayUsage > 0 ? CommercialUsageStatus.AtLimit : CommercialUsageStatus.Available;
  }

  const usageRatio = displayUsage / allowance;
  if (usageRatio >= 1) {
    return CommercialUsageStatus.AtLimit;
  }

  if (usageRatio >= 0.75) {
    return CommercialUsageStatus.Nearing;
  }

  return CommercialUsageStatus.Available;
};

const adaptMeter = ({
  key,
  meter,
}: {
  key: CommercialMeterKey;
  meter: z.infer<typeof commercialUsageMeterSchema>;
}): CommercialUsageMeterSnapshot => ({
  allowance: meter.allowance,
  current: meter.current,
  key,
  kind: meter.kind,
  peak: meter.peak,
  resetAt: meter.reset_at,
  status: getUsageStatus(meter),
  used: meter.used,
});

/**
 * Parses the additive PlatformInfo `commercial_plan_snapshot` field.
 *
 * Legacy and partially rolled-out backends are expected to omit the field. A
 * malformed snapshot is also treated as unavailable so billing UI never
 * guesses allowances or presents a plan-change action from incomplete data.
 */
export const parseCommercialPlanSnapshot = (value: unknown): CommercialPlanSnapshot | null => {
  const parsed = commercialPlanSnapshotSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }

  const meters = Object.fromEntries(
    COMMERCIAL_METER_KEYS.map(key => [key, adaptMeter({ key, meter: parsed.data.meters[key] })])
  ) as CommercialPlanSnapshot["meters"];

  const pendingChange = parsed.data.pending_change;
  if (
    pendingChange?.destination_plan_key &&
    !isCommercialPlanKey(pendingChange.destination_plan_key)
  ) {
    return null;
  }

  if (
    pendingChange &&
    !pendingChange.destination_plan_key &&
    (pendingChange.paid_extra_usage_blocks === null ||
      pendingChange.paid_extra_usage_blocks === undefined)
  ) {
    return null;
  }

  const recommendedPlanKey = parsed.data.migration?.recommended_plan_key;
  if (recommendedPlanKey && !isCommercialPlanKey(recommendedPlanKey)) {
    return null;
  }

  return {
    blockPolicy: parsed.data.block_policy
      ? {
          available: parsed.data.block_policy.available,
          complimentaryBlocks: parsed.data.block_policy.complimentary_blocks,
          maxPaidBlocks: parsed.data.block_policy.max_paid_blocks ?? null,
          mode: parsed.data.block_policy.mode,
          paidBlocks: parsed.data.block_policy.paid_blocks,
          priceCents: parsed.data.block_policy.price_cents,
        }
      : null,
    catalogGeneration: parsed.data.catalog_generation,
    memProTrialEligible: parsed.data.mem_pro_trial_eligible,
    currentPlan: {
      currency: parsed.data.current_plan.currency,
      displayName: parsed.data.current_plan.display_name,
      isLegacy: parsed.data.current_plan.is_legacy,
      key: parsed.data.current_plan.key,
      monthlyPriceCents: parsed.data.current_plan.monthly_price_cents,
    },
    meters,
    migration: parsed.data.migration
      ? {
          changedAllowances: parsed.data.migration.changed_allowances?.map(change => ({
            changeKind: change.change_kind,
            currentDisplay: change.current_display,
            meterKey: change.meter_key as CommercialMeterKey,
            newAllowance: change.new_allowance,
          })),
          canReturn: parsed.data.migration.can_return,
          eligible: parsed.data.migration.eligible,
          limitingMeterKey: parsed.data.migration.limiting_meter_key
            ? (parsed.data.migration.limiting_meter_key as CommercialMeterKey)
            : null,
          recommendedPlanKey: recommendedPlanKey
            ? (recommendedPlanKey as CommercialPlanKeyType)
            : null,
          recommendedPaidExtraUsageBlocks:
            parsed.data.migration.recommended_paid_extra_usage_blocks ?? null,
          sourceMonthlyPriceCents: parsed.data.migration.source_monthly_price_cents ?? null,
          sourcePlanDisplayName: parsed.data.migration.source_plan_display_name ?? null,
          state: parsed.data.migration.state as CommercialMigrationState,
        }
      : null,
    pausedConnectionIds: parsed.data.paused_connection_ids,
    pausedWorkingMemoryItems: parsed.data.paused_working_memory_items.map(item => ({
      id: item.item_id,
      kind: item.item_kind,
    })),
    pendingChange: pendingChange
      ? {
          destinationPlanKey: pendingChange.destination_plan_key
            ? (pendingChange.destination_plan_key as CommercialPlanKeyType)
            : null,
          effectiveAt: pendingChange.effective_at,
          paidExtraUsageBlocks: pendingChange.paid_extra_usage_blocks ?? null,
          status: pendingChange.status,
        }
      : null,
    period: {
      endsAt: parsed.data.period.ends_at,
      startsAt: parsed.data.period.starts_at,
    },
  };
};
